/*
 * Scenario-Helpers — werden von jedem Scenario-Modul ueber den state-
 * Parameter verwendet. Halten das Boilerplate in Scenarios klein.
 */

import { markdownToRichText } from '../../RichTextOps.js'

/**
 * Event-Name -> Handler-Name. Der Scenario-Autor schreibt "proposeRewrite",
 * der Transport ruft state.handlers.onProposeRewrite(payload) auf.
 */
const HANDLER_MAP = Object.freeze({
    session: 'onSession',
    thinking: 'onThinking',
    thinkingDone: 'onThinkingDone',
    delta: 'onDelta',
    toolCall: 'onToolCall',
    toolResult: 'onToolResult',
    proposeRewrite: 'onProposeRewrite',
    proposeInsert: 'onProposeInsert',
    proposeDelete: 'onProposeDelete',
    // Phase 3: Auto-Apply (Background-Inhalt + Sketch + Note + Sections)
    proposeRewriteApply: 'onProposeRewriteApply',
    proposeInsertApply: 'onProposeInsertApply',
    proposeDeleteApply: 'onProposeDeleteApply',
    proposeSketch: 'onProposeSketch',
    proposeWriteNote: 'onProposeWriteNote',
    proposeCreateSection: 'onProposeCreateSection',
    proposeUpdateSection: 'onProposeUpdateSection',
    proposeDeleteSection: 'onProposeDeleteSection',
    // Phase 4
    proposeUpdateUserMemory: 'onProposeUpdateUserMemory',
    proposeUpdateContextInfo: 'onProposeUpdateContextInfo',
    proposeJumpToSection: 'onProposeJumpToSection',
    rateLimit: 'onRateLimit',
    done: 'onDone',
    error: 'onError',
    aborted: 'onAborted'
})

/**
 * Wartet delayMs Millisekunden und ruft dann den passenden Handler mit payload.
 * Respektiert das Abort-Signal: wenn abort feuert, wirft ein AbortError und
 * der pending setTimeout wird geloescht.
 *
 * Scenarios rufen `await emit(state, 'proposeRewrite', {...}, 300)`.
 *
 * @param {object} state - Scenario-State (mit handlers, abortSig)
 * @param {string} eventName - Name laut HANDLER_MAP
 * @param {object} payload - An den Handler weitergereicht
 * @param {number} [delayMs=0]
 * @returns {Promise<void>}
 */
export function emit(state, eventName, payload = {}, delayMs = 0) {
    const handlerName = HANDLER_MAP[eventName]
    if (!handlerName) {
        return Promise.reject(new Error(`Unknown scenario event: ${eventName}`))
    }
    const resolvedPayload = resolveDeep(state, payload)

    return new Promise((resolve, reject) => {
        if (state.abortSig?.signal.aborted) {
            return reject(abortError())
        }
        const timer = setTimeout(() => {
            state.abortSig?.signal.removeEventListener('abort', onAbort)
            const fn = state.handlers?.[handlerName]
            if (typeof fn === 'function') {
                try { fn(resolvedPayload) } catch (err) { return reject(err) }
            }
            resolve()
        }, delayMs * (state.delayScale ?? 1))

        const onAbort = () => {
            clearTimeout(timer)
            reject(abortError())
        }
        state.abortSig?.signal.addEventListener('abort', onAbort, { once: true })
    })
}

/**
 * Wartet auf ein reportActionDone({toolCallId}) und gibt das Ergebnis zurueck.
 * Wird vom reaktiven Scenario verwendet (z.B. accept-then-undo).
 *
 * @param {object} state
 * @param {string} toolCallId
 * @param {number} [timeoutMs=30000]
 * @returns {Promise<{result: string, appliedVersion?: number}>}
 */
export function wait(state, toolCallId, timeoutMs = 30000) {
    // Sofort-Resolve wenn das Ergebnis schon eingetroffen ist
    if (state.toolResults.has(toolCallId)) {
        return Promise.resolve(state.toolResults.get(toolCallId))
    }
    return new Promise((resolve, reject) => {
        if (state.abortSig?.signal.aborted) {
            return reject(abortError())
        }
        const timer = setTimeout(() => {
            state.toolResolvers.delete(toolCallId)
            state.abortSig?.signal.removeEventListener('abort', onAbort)
            reject(new Error(`Timeout waiting for action-done on ${toolCallId}`))
        }, timeoutMs)
        const onAbort = () => {
            clearTimeout(timer)
            state.toolResolvers.delete(toolCallId)
            reject(abortError())
        }
        state.abortSig?.signal.addEventListener('abort', onAbort, { once: true })
        state.toolResolvers.set(toolCallId, {
            resolve: (info) => { clearTimeout(timer); resolve(info) },
            reject
        })
    })
}

/**
 * Loest einen Placeholder-String wie `$cursor.paragraphId` aus dem Context auf.
 * Unbekannte Paths werden unveraendert zurueckgegeben (damit ein Scenario ohne
 * Cursor-Context trotzdem laeuft — es bekommt dann den literalen String).
 *
 * Bekannte Placeholder (Phase A, erweiterbar):
 *   $cursor.paragraphId   -> context.cursorContext.paragraphId
 *   $cursor.separatorId   -> context.cursorContext.separatorId
 *   $cursor.text          -> context.cursorContext.text (optional; fuer Tests)
 *   $cursor.version       -> context.cursorContext.version (optional)
 *   $cursor.pane          -> context.cursorContext.pane
 *
 * @param {object} state
 * @param {string} placeholder - muss mit '$' beginnen
 * @returns {string|number|null}
 */
export function resolve(state, placeholder) {
    if (typeof placeholder !== 'string' || !placeholder.startsWith('$')) {
        return placeholder
    }
    const path = placeholder.slice(1).split('.')
    // Root 'cursor' -> state.context.cursorContext
    let node
    if (path[0] === 'cursor') {
        node = state.context?.cursorContext
        for (let i = 1; i < path.length; i++) {
            if (node == null) return placeholder
            node = node[path[i]]
        }
        return node === undefined ? placeholder : node
    }
    // Root 'context' -> state.context
    if (path[0] === 'context') {
        node = state.context
        for (let i = 1; i < path.length; i++) {
            if (node == null) return placeholder
            node = node[path[i]]
        }
        return node === undefined ? placeholder : node
    }
    return placeholder
}

/** Rekursives resolve auf allen String-Werten eines Payload-Objekts. */
function resolveDeep(state, value) {
    if (typeof value === 'string' && value.startsWith('$')) {
        return resolve(state, value)
    }
    if (Array.isArray(value)) {
        return value.map(v => resolveDeep(state, v))
    }
    if (value && typeof value === 'object') {
        const out = {}
        for (const [k, v] of Object.entries(value)) {
            out[k] = resolveDeep(state, v)
        }
        return out
    }
    return value
}

function abortError() {
    const err = new Error('Scenario aborted')
    err.name = 'AbortError'
    return err
}

/**
 * Parst einen Markdown-String im Carbisdale-Dialekt zu einem Patch, den
 * Scenarios in Tool-Payloads verteilen. Eine Quelle der Wahrheit
 * (`markdownToRichText` aus RichTextOps.js) — analog zum Server-Pfad in
 * `server/agent/markdown.js#markdownToFieldPatch`.
 *
 * Dialekt: **bold**, *italic*, _underline_, ~~strike~~, `code`, [[@id]] (Section-Link),
 * # bis ###### Heading 1-6, - Bullet (2 Spaces / Level), 1. Numbered, > Blockquote.
 *
 * Field-Awareness:
 *   'text'         — voller Patch inkl. Block-Felder (Default)
 *   'note'         — nur Inline-Formate + Links (Notes haben keine Block-Formate)
 *   'sectionTitle' — Plaintext, alles andere ignoriert
 *
 * Rueckgabe-Shape je Field:
 *   text         → { text, formats, links, headingLevel, listType, listLevel, blockquote }
 *   note         → { text, formats, links }
 *   sectionTitle → { text }
 *
 * @param {string} markdown
 * @param {'text'|'note'|'sectionTitle'} [fieldType='text']
 * @returns {object}
 */
export function payloadFromMarkdown(markdown, fieldType = 'text') {
    const rt = markdownToRichText(typeof markdown === 'string' ? markdown : '')
    if (fieldType === 'note') {
        return {
            text: rt.text,
            formats: Array.isArray(rt.formats) ? rt.formats : [],
            links: Array.isArray(rt.links) ? rt.links : []
        }
    }
    if (fieldType === 'sectionTitle') {
        return { text: rt.text }
    }
    // 'text' — voller Patch (Block-Felder mit Defaults)
    return {
        text: rt.text,
        formats: Array.isArray(rt.formats) ? rt.formats : [],
        links: Array.isArray(rt.links) ? rt.links : [],
        headingLevel: rt.headingLevel || 0,
        listType: rt.listType || null,
        listLevel: rt.listLevel || 0,
        blockquote: !!rt.blockquote
    }
}
